import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { canPrepareUC, canFinaliseUC } from "@/lib/roles";
import {
  FileDown, FileSpreadsheet, ShieldCheck, AlertTriangle, CheckCircle2,
  Lock, Unlock, Save, ChevronDown, ChevronRight, Info,
} from "lucide-react";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const currentFY = () => {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `FY ${start}-${String((start + 1) % 100).padStart(2, "0")}`;
};
const fyOptions = () => {
  const now = new Date().getFullYear();
  const arr = [];
  for (let s = now + 1; s >= now - 5; s--) arr.push(`FY ${s}-${String((s + 1) % 100).padStart(2, "0")}`);
  return arr;
};

const downloadBlob = async (url, filename, token) => {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
};

export default function UtilisationCertificate() {
  const { user } = useAuth();
  const canPrepare = canPrepareUC(user?.role);
  const canFinal = canFinaliseUC(user?.role);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [periodType, setPeriodType] = useState("fy");
  const [fy, setFy] = useState(currentFY());
  const [quarter, setQuarter] = useState("1");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [manualInterest, setManualInterest] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [preview, setPreview] = useState(null);
  const [savedUCId, setSavedUCId] = useState(null);
  const [savedStatus, setSavedStatus] = useState(null);
  const [savedVersion, setSavedVersion] = useState(1);
  const [busy, setBusy] = useState(false);
  const [register, setRegister] = useState([]);
  const [tab, setTab] = useState("prepare");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [expandedCat, setExpandedCat] = useState({});
  const [expandedHead, setExpandedHead] = useState({});

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : "";

  useEffect(() => {
    if (!canPrepare) return;
    api.get("/projects").then(r => {
      const list = r.data.filter(p => p.project_code);
      setProjects(list);
      if (list[0]) setProjectId(list[0].id);
    });
    api.get("/v2/uc-templates").then(r => {
      setTemplates(r.data);
      const def = r.data.find(t => t.is_default);
      setTemplateId(def?.id || "");
    }).catch(() => {});
  }, [canPrepare]);

  const loadRegister = () => {
    if (!projectId) { setRegister([]); return; }
    api.get(`/v2/uc?project_id=${projectId}`).then(r => setRegister(r.data)).catch(() => setRegister([]));
  };
  useEffect(() => { loadRegister(); }, [projectId]);

  const period_ref = useMemo(() => {
    if (periodType === "fy") return fy;
    if (periodType === "quarter") return `${fy} Q${quarter}`;
    if (periodType === "custom") return `${customStart}..${customEnd}`;
    return "";
  }, [periodType, fy, quarter, customStart, customEnd]);

  const isPreviewable = projectId && (
    periodType !== "custom" || (customStart && customEnd)
  );

  const generate = async ({ save }) => {
    if (!isPreviewable) return;
    setBusy(true);
    try {
      const r = await api.post("/v2/uc/generate", {
        project_id: projectId,
        period_type: periodType,
        period_ref,
        template_id: templateId || null,
        manual_interest: Number(manualInterest) || 0,
        save,
      });
      setPreview(r.data);
      if (save) {
        setSavedUCId(r.data.uc_id);
        setSavedStatus("Draft");
        setSavedVersion(1);
        toast.success("UC draft saved");
        loadRegister();
      } else {
        setSavedUCId(null); setSavedStatus(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "UC generation failed");
    } finally { setBusy(false); }
  };

  const openSavedUC = async (uc) => {
    setBusy(true);
    try {
      const r = await api.get(`/v2/uc/${uc.id}`);
      setPreview(r.data.snapshot);
      setSavedUCId(r.data.id);
      setSavedStatus(r.data.status);
      setSavedVersion(r.data.version || 1);
      setProjectId(r.data.project_id);
      setPeriodType(r.data.period_type);
      // period_ref restore best-effort
      if (r.data.period_type === "fy") setFy(r.data.period_ref);
      if (r.data.period_type === "quarter") {
        const [f, q] = r.data.period_ref.split(" Q");
        setFy(f); setQuarter(q || "1");
      }
      if (r.data.period_type === "custom" && r.data.period_ref?.includes("..")) {
        const [s, e] = r.data.period_ref.split("..");
        setCustomStart(s); setCustomEnd(e);
      }
      setTab("prepare");
    } catch (err) { toast.error("Load failed"); }
    finally { setBusy(false); }
  };

  const finalise = async () => {
    if (!savedUCId) return;
    if (!window.confirm("Finalise this UC? Every approved expense in the period will be period-locked. Only Admin can reopen.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/v2/uc/${savedUCId}/finalise`, { prepared_by_note: "" });
      setSavedStatus(r.data.status);
      setSavedVersion(r.data.version || 1);
      toast.success("UC finalised. Period locked.");
      loadRegister();
    } catch (err) { toast.error(err.response?.data?.detail || "Finalise failed"); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    if (!savedUCId) return;
    if (reopenReason.trim().length < 5) { toast.error("Reason must be at least 5 characters"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/v2/uc/${savedUCId}/reopen`, { reason: reopenReason });
      setSavedStatus(r.data.status);
      setSavedVersion(r.data.version || 1);
      setReopenOpen(false); setReopenReason("");
      toast.success(`UC reopened. Now Draft v${r.data.version}.`);
      loadRegister();
    } catch (err) { toast.error(err.response?.data?.detail || "Reopen failed"); }
    finally { setBusy(false); }
  };

  const doExport = async (kind) => {
    if (!savedUCId) { toast.error("Save the UC as Draft first"); return; }
    try {
      const url = `${API}/v2/uc/${savedUCId}/export/${kind}`;
      const ext = kind === "xlsx" ? "xlsx" : "pdf";
      await downloadBlob(url, `UC-${savedUCId.slice(0, 8)}.${ext}`, token);
    } catch { toast.error("Export failed"); }
  };

  if (!canPrepare) {
    return <div className="p-8 text-sm text-slate-500">Only Super Admin or Accounts can access the Utilisation Certificate module.</div>;
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Statutory · Donor Reporting</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Utilisation Certificate</h1>
          <p className="text-sm text-slate-500 mt-1">
            Reports actual expenditure against the <b>Sanctioned</b> budget. Working budget is ignored.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="prepare" data-testid="uc2-tab-prepare">Prepare</TabsTrigger>
          <TabsTrigger value="register" data-testid="uc2-tab-register">Register ({register.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="prepare">
          <Card className="mb-4">
            <CardContent className="p-5 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div className="md:col-span-2">
                <Label>Project</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger data-testid="uc2-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period type</Label>
                <Select value={periodType} onValueChange={setPeriodType}>
                  <SelectTrigger data-testid="uc2-period-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fy">Financial Year</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {periodType !== "custom" && (
                <div>
                  <Label>Financial Year</Label>
                  <Select value={fy} onValueChange={setFy}>
                    <SelectTrigger data-testid="uc2-fy-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{fyOptions().map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {periodType === "quarter" && (
                <div>
                  <Label>Quarter</Label>
                  <Select value={quarter} onValueChange={setQuarter}>
                    <SelectTrigger data-testid="uc2-quarter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Q1 (Apr–Jun)</SelectItem>
                      <SelectItem value="2">Q2 (Jul–Sep)</SelectItem>
                      <SelectItem value="3">Q3 (Oct–Dec)</SelectItem>
                      <SelectItem value="4">Q4 (Jan–Mar)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {periodType === "custom" && (
                <>
                  <div><Label>From (YYYY-MM)</Label><Input value={customStart} onChange={e => setCustomStart(e.target.value)} placeholder="2026-04" data-testid="uc2-custom-start" /></div>
                  <div><Label>To (YYYY-MM)</Label><Input value={customEnd} onChange={e => setCustomEnd(e.target.value)} placeholder="2026-09" data-testid="uc2-custom-end" /></div>
                </>
              )}
              <div>
                <Label>Template</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger data-testid="uc2-template-select"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Interest (manual, ₹)</Label>
                <Input type="number" value={manualInterest} onChange={e => setManualInterest(e.target.value)} placeholder="0" data-testid="uc2-manual-interest" />
              </div>
            </CardContent>
            <div className="px-5 pb-5 flex flex-wrap gap-2">
              <Button onClick={() => generate({ save: false })} disabled={!isPreviewable || busy} variant="outline" data-testid="uc2-preview-btn">
                <Info className="w-4 h-4 mr-1" /> Preview
              </Button>
              <Button onClick={() => generate({ save: true })} disabled={!isPreviewable || busy || savedStatus === "Final"} className="bg-[#0F172A]" data-testid="uc2-save-draft-btn">
                <Save className="w-4 h-4 mr-1" /> Save Draft
              </Button>
              <Button onClick={() => doExport("xlsx")} disabled={!savedUCId} variant="outline" data-testid="uc2-export-xlsx-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
              <Button onClick={() => doExport("pdf")} disabled={!savedUCId} variant="outline" data-testid="uc2-export-pdf-btn">
                <FileDown className="w-4 h-4 mr-1" /> PDF
              </Button>
              {canFinal && savedStatus === "Draft" && (
                <Button onClick={finalise} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="uc2-finalise-btn">
                  <Lock className="w-4 h-4 mr-1" /> Finalise & Lock
                </Button>
              )}
              {canFinal && savedStatus === "Final" && (
                <Button onClick={() => setReopenOpen(true)} disabled={busy} variant="outline" className="border-amber-400 text-amber-700" data-testid="uc2-reopen-btn">
                  <Unlock className="w-4 h-4 mr-1" /> Reopen
                </Button>
              )}
              {savedUCId && (
                <div className="ml-auto flex items-center gap-2 text-xs">
                  <Badge variant={savedStatus === "Final" ? "default" : "outline"} className={savedStatus === "Final" ? "bg-emerald-700" : ""}>
                    {savedStatus} · v{savedVersion}
                  </Badge>
                  <span className="text-slate-500 font-mono">{savedUCId.slice(0, 8)}…</span>
                </div>
              )}
            </div>
          </Card>

          {preview && <PreviewBlock preview={preview} expandedCat={expandedCat} setExpandedCat={setExpandedCat} expandedHead={expandedHead} setExpandedHead={setExpandedHead} />}
        </TabsContent>

        <TabsContent value="register">
          <Card>
            <CardContent className="p-0">
              {register.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-500">No UCs saved yet for this project.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                    <tr className="border-b">
                      <th className="text-left px-3 py-2">Period</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Version</th>
                      <th className="text-left px-3 py-2">Prepared</th>
                      <th className="text-left px-3 py-2">Finalised</th>
                      <th className="text-right px-3 py-2">Closing (₹)</th>
                      <th className="text-right px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid="uc2-register-table">
                    {register.map(u => (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-mono text-xs">{u.period_ref} <span className="text-slate-400">({u.start_ym}→{u.end_ym})</span></td>
                        <td className="px-3 py-2"><Badge variant={u.status === "Final" ? "default" : "outline"} className={u.status === "Final" ? "bg-emerald-700" : ""}>{u.status}</Badge></td>
                        <td className="px-3 py-2 font-mono text-xs">v{u.version || 1}{u.snapshot_invalidated ? <span className="ml-1 text-amber-600">(superseded)</span> : ""}</td>
                        <td className="px-3 py-2 text-xs">{u.prepared_by_name} <span className="text-slate-400 block">{u.prepared_at?.slice(0, 10)}</span></td>
                        <td className="px-3 py-2 text-xs">{u.finalised_by_name || "—"} <span className="text-slate-400 block">{u.finalised_at?.slice(0, 10)}</span></td>
                        <td className="px-3 py-2 text-right font-mono">{rupee(u.closing_balance)}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => openSavedUC(u)} data-testid={`uc2-open-${u.id.slice(0,8)}`}>Open</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reopen UC — reason required</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">The current finalised snapshot will be archived and marked as superseded. Period-locks on all expenses will be released.</div>
          <Textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)} placeholder="e.g. Late invoice from vendor discovered — adjusting cumulative actuals" rows={4} data-testid="uc2-reopen-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button onClick={reopen} disabled={busy || reopenReason.trim().length < 5} className="bg-amber-600 hover:bg-amber-700" data-testid="uc2-reopen-confirm">Reopen (creates v{savedVersion + 1})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewBlock({ preview, expandedCat, setExpandedCat, expandedHead, setExpandedHead }) {
  const gt = preview.grand_totals || {};
  const fs = preview.funds_section || {};
  const pct = gt.sanctioned > 0 ? (gt.cumulative / gt.sanctioned * 100) : 0;
  return (
    <div className="space-y-4" data-testid="uc2-preview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniCard label="Sanctioned Total" value={rupee(gt.sanctioned)} tone="slate" />
        <MiniCard label="Actual (period)" value={rupee(gt.actual_period)} tone="sky" />
        <MiniCard label="Cumulative to Date" value={rupee(gt.cumulative)} tone="emerald" />
        <MiniCard label="Utilised %" value={`${pct.toFixed(1)}%`} tone={pct > 100 ? "rose" : pct > 90 ? "amber" : "emerald"} />
      </div>

      {preview.flags?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
              <AlertTriangle className="w-4 h-4" /> Red Flags ({preview.flags.length})
            </div>
            <ul className="text-xs space-y-1 text-amber-900">
              {preview.flags.map((f, i) => <li key={i}>• {flagLabel(f)}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}
      {preview.reconciled && (
        <div className="text-xs text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Funds reconciled — expenditure ties to grand actual.</div>
      )}

      <Card>
        <CardHeader className="py-3 border-b bg-slate-50"><CardTitle className="text-sm">Line Table — Category → Head → Sub-Type</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr className="border-b">
                <th className="text-left px-3 py-2">Line</th>
                <th className="text-right px-3 py-2">Sanctioned</th>
                <th className="text-right px-3 py-2">Actual (Period)</th>
                <th className="text-right px-3 py-2">Cumulative</th>
                <th className="text-right px-3 py-2">Balance</th>
                <th className="text-right px-3 py-2">% Util</th>
              </tr>
            </thead>
            <tbody data-testid="uc2-line-table">
              {(preview.line_table || []).map(cat => {
                const catKey = cat.category.code;
                const catOpen = expandedCat[catKey] !== false;
                return (
                  <React.Fragment key={catKey}>
                    <tr className="bg-slate-100 font-semibold cursor-pointer" onClick={() => setExpandedCat(s => ({ ...s, [catKey]: !catOpen }))}>
                      <td className="px-3 py-2 flex items-center gap-1">{catOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}<span>{cat.category.name}</span></td>
                      <td className="px-3 py-2 text-right font-mono">{rupee(cat.totals.sanctioned)}</td>
                      <td className="px-3 py-2 text-right font-mono">{rupee(cat.totals.actual_period)}</td>
                      <td className="px-3 py-2 text-right font-mono">{rupee(cat.totals.cumulative)}</td>
                      <td className="px-3 py-2 text-right font-mono">{rupee(cat.totals.sanctioned - cat.totals.cumulative)}</td>
                      <td className="px-3 py-2 text-right font-mono">{cat.totals.sanctioned > 0 ? ((cat.totals.cumulative / cat.totals.sanctioned) * 100).toFixed(1) : "—"}%</td>
                    </tr>
                    {catOpen && cat.heads.map(head => {
                      const hKey = `${catKey}:${head.id}`;
                      const hOpen = expandedHead[hKey] === true;
                      return (
                        <React.Fragment key={hKey}>
                          <tr className="bg-slate-50 cursor-pointer" onClick={() => setExpandedHead(s => ({ ...s, [hKey]: !hOpen }))}>
                            <td className="px-3 py-2 pl-8 flex items-center gap-1">{hOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}<span>{head.code} · {head.name}</span></td>
                            <td className="px-3 py-2 text-right font-mono">{rupee(head.totals.sanctioned)}</td>
                            <td className="px-3 py-2 text-right font-mono">{rupee(head.totals.actual_period)}</td>
                            <td className="px-3 py-2 text-right font-mono">{rupee(head.totals.cumulative)}</td>
                            <td className="px-3 py-2 text-right font-mono">{rupee(head.totals.sanctioned - head.totals.cumulative)}</td>
                            <td className="px-3 py-2 text-right font-mono">{head.totals.sanctioned > 0 ? ((head.totals.cumulative / head.totals.sanctioned) * 100).toFixed(1) : "—"}%</td>
                          </tr>
                          {hOpen && head.subtypes.map(s => (
                            <tr key={s.subtype_id} className="border-b border-slate-100">
                              <td className="px-3 py-2 pl-16 text-xs text-slate-600">{s.subtype_code} · {s.subtype_name}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{rupee(s.sanctioned_total)}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{rupee(s.actual_period)}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{rupee(s.cumulative_actual)}</td>
                              <td className={`px-3 py-2 text-right font-mono text-xs ${s.balance < 0 ? "text-rose-600" : ""}`}>{rupee(s.balance)}</td>
                              <td className={`px-3 py-2 text-right font-mono text-xs ${s.utilised_pct > 100 ? "text-rose-600 font-bold" : ""}`}>{s.utilised_pct.toFixed(1)}%</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <tr className="bg-amber-50 font-bold border-t-2 border-amber-300">
                <td className="px-3 py-2">GRAND TOTAL</td>
                <td className="px-3 py-2 text-right font-mono" data-testid="uc2-grand-sanctioned">{rupee(gt.sanctioned)}</td>
                <td className="px-3 py-2 text-right font-mono" data-testid="uc2-grand-period">{rupee(gt.actual_period)}</td>
                <td className="px-3 py-2 text-right font-mono" data-testid="uc2-grand-cumulative">{rupee(gt.cumulative)}</td>
                <td className="px-3 py-2 text-right font-mono">{rupee(gt.sanctioned - gt.cumulative)}</td>
                <td className="px-3 py-2 text-right font-mono">{pct.toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3 border-b bg-slate-50"><CardTitle className="text-sm">Funds Section</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <Field label="Opening Balance" value={rupee(fs.opening_balance)} />
          <Field label="Grant Receipts in Period" value={rupee(fs.grant_total)} sub={`${fs.grant_receipts?.length || 0} instalment(s)`} />
          <Field label={`Interest (${fs.interest_source})`} value={rupee(fs.interest)} />
          <Field label="Total Funds Available" value={rupee(fs.total_funds_available)} strong />
          <Field label="Expenditure in Period" value={rupee(fs.expenditure_in_period)} />
          <Field label="Closing Balance" value={rupee(fs.closing_balance)} strong />
          {fs.grant_receipts?.length > 0 && (
            <div className="col-span-full mt-2">
              <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Grant Receipts</div>
              <table className="w-full text-xs">
                <thead className="text-slate-500 border-b"><tr><th className="text-left py-1">Date</th><th className="text-left">Instalment</th><th className="text-right">Amount</th><th className="text-left pl-3">Bank Ref</th></tr></thead>
                <tbody>
                  {fs.grant_receipts.map(g => <tr key={g.id} className="border-b border-slate-100"><td className="py-1 font-mono">{g.receipt_date}</td><td>{g.instalment_ref || "—"}</td><td className="text-right font-mono">{rupee(g.amount)}</td><td className="pl-3 font-mono text-slate-500">{g.bank_reference || "—"}</td></tr>)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <OpenAdvancesAnnexure data={preview.open_advances_annexure} />

      {preview.pending_not_included?.length > 0 && (
        <Card className="border-slate-300">
          <CardHeader className="py-3 border-b bg-slate-50"><CardTitle className="text-sm text-slate-700">Pending — NOT included in this UC ({preview.pending_not_included.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50"><tr className="border-b"><th className="text-left px-3 py-2">Kind</th><th className="text-left px-3 py-2">Invoice</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Sub-Type</th></tr></thead>
              <tbody>
                {preview.pending_not_included.map(p => <tr key={p.id} className="border-b border-slate-100"><td className="px-3 py-2">{p.kind}</td><td className="px-3 py-2 font-mono text-xs">{p.invoice_no || "—"}</td><td className="px-3 py-2 text-right font-mono">{rupee(p.amount)}</td><td className="px-3 py-2 font-mono text-xs">{p.subtype_code || "—"}</td></tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const OpenAdvancesAnnexure = ({ data }) => {
  if (!data) return null;
  const th = "px-3 py-2";
  return (
    <Card className="border-amber-300" data-testid="uc-open-advances-annexure">
      <CardHeader className="py-3 border-b bg-amber-50">
        <CardTitle className="text-sm text-amber-900">Annexure — Open Advances (as on {data.as_on}) · {data.rows.length} · Outstanding {rupee(data.total_outstanding)}</CardTitle>
        <div className="text-xs text-amber-800">{data.note}</div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50"><tr className="border-b">
            <th className={`text-left ${th}`}>Advance</th><th className={`text-left ${th}`}>Requester</th><th className={`text-left ${th}`}>Disbursed</th>
            <th className={`text-left ${th}`}>Purpose</th><th className={`text-left ${th}`}>Budget line</th><th className={`text-right ${th}`}>Amount</th>
            <th className={`text-right ${th}`}>Settled</th><th className={`text-right ${th}`}>Outstanding</th><th className={`text-right ${th}`}>Age (days)</th>
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={9} className="text-center px-3 py-6 text-slate-500" data-testid="uc-open-advances-empty">No open advances for this project.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.advance_id} className="border-b border-slate-100" data-testid={`uc-open-adv-${r.advance_id}`}>
                <td className={`${th} font-mono text-xs`}>{r.advance_id}</td><td className={th}>{r.requester_name}</td><td className={`${th} font-mono text-xs`}>{r.disbursed_on || "—"}</td>
                <td className={th}>{r.purpose || "—"}</td><td className={`${th} text-xs`}>{r.budget_line || "—"}</td><td className={`${th} text-right font-mono`}>{rupee(r.amount)}</td>
                <td className={`${th} text-right font-mono`}>{rupee(r.settled)}</td><td className={`${th} text-right font-mono font-semibold`}>{rupee(r.outstanding)}</td>
                <td className={`${th} text-right font-mono ${r.age_days > 30 ? "text-red-700 font-semibold" : ""}`}>{r.age_days ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

const flagLabel = (f) => {
  if (!f || typeof f !== "object") return String(f);
  switch (f.type) {
    case "over_sanctioned_subtype":
      return `Sub-type ${f.subtype} exceeds sanctioned — cumulative ₹${Number(f.cumulative || 0).toLocaleString("en-IN")} > sanctioned ₹${Number(f.sanctioned || 0).toLocaleString("en-IN")}`;
    case "head_deviation_over_10pct":
      return `Head ${f.head} deviates ${f.deviation_pct}% from sanctioned (₹${Number(f.cumulative || 0).toLocaleString("en-IN")} vs ₹${Number(f.sanctioned || 0).toLocaleString("en-IN")})`;
    case "expenses_on_unknown_subtypes":
      return `${f.count} expense(s) reference sub-types not in the sanctioned budget`;
    case "open_advances_unsettled":
      return `${f.count} advance(s) still unsettled — ₹${Number(f.outstanding || 0).toLocaleString("en-IN")} outstanding (see Open Advances annexure; not counted as expenditure)`;
    case "expenditure_exceeds_funds":
      return `Expenditure ₹${Number(f.expenditure || 0).toLocaleString("en-IN")} exceeds funds available ₹${Number(f.funds_available || 0).toLocaleString("en-IN")}`;
    default:
      return JSON.stringify(f);
  }
};

const MiniCard = ({ label, value, tone = "slate" }) => (
  <Card className={`border-${tone}-200`}>
    <CardContent className="p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`mt-1 font-display font-bold text-lg text-${tone}-800`}>{value}</div>
    </CardContent>
  </Card>
);

const Field = ({ label, value, sub, strong }) => (
  <div className="rounded-md border border-slate-200 p-3">
    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wider">{label}</div>
    <div className={`mt-1 font-mono ${strong ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);
